/* @flow */
import pluralize from 'pluralize';
import * as React from 'react';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { graphql, compose } from 'react-apollo';
import { showToast } from 'reducers/toasts';
import { each, capitalize, values } from 'lodash';

import Button from 'components/Button';
import CurrentUser from 'auth/CurrentUser';
import Divider from 'components/Divider';
import FreeClassMembershipLimitNoticeContainer from 'groups/components/FreeClassMembershipLimitNotice';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Notice from 'components/Notice';
import RecipientParser from 'lib/RecipientParser';
import Row from 'components/Row';
import TabPanel from 'components/TabPanel';
import Tabs from 'components/Tabs';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import TitleHeader from 'components/TitleHeader';
import Tooltip from 'components/Tooltip';
import Group from 'entities/Group';
import UsersParticipantsTableInput from 'users/components/UsersInviteModal/UsersInvite/UsersParticipantsTableInput';
import sisSyncPath from 'admin/lib/sisSyncPath';
import {
  userRowEmpty,
  DIRECT_ADD_USER_ROLES,
  setMaxRowsForEntity,
  MAXIMUM_ROWS,
  handleServerErrors,
} from 'reducers/directAddForm';

import type {
  UserRow,
  SetMaxRowsForEntity,
  HandleServerErrors,
  ServerUserError,
} from 'reducers/directAddForm';

type DirectAddUserData = {
  name: string,
  role: string,
  devices: Array<string>,
};

type Props = {
  group: Group,
  supportSms: boolean,
  showToast: Function,
  onClose: () => any,
  setMaxRowsForEntity: (entity: 'group' | 'organization') => SetMaxRowsForEntity,
  handleServerErrors: (errors: ServerUserError[]) => HandleServerErrors,

  // from Redux state
  totalNumberOfUsersToAdd: number,
  usersByRole: {
    students: Array<UserRow>,
    parents: Array<UserRow>,
    teachers: Array<UserRow>,
  },
  containsData: boolean,

  // from GQL
  directAddUsers: (
    targetModel: string,
    targetId: number,
    users: Array<DirectAddUserData>
  ) => Promise<*>,
};

type ErrorSummary = {
  student: number,
  parent: number,
  teacher: number,
};

type State = {
  loading: boolean,
  errorCountFromServer: number,
  directAddSuccessCountFromServer: number,
  invitationSuccessCountFromServer: number,
};

export function successMessage(
  numDirectAddSuccesses: number,
  numInvitationSuccesses: number
): string {
  const directAddCopy =
    numDirectAddSuccesses > 0
      ? `${numDirectAddSuccesses} ${pluralize('person', numDirectAddSuccesses)} added.`
      : '';
  const invitationCopy =
    numInvitationSuccesses > 0
      ? `Invitations were sent to ${numInvitationSuccesses} ${pluralize(
        'person',
        numInvitationSuccesses
      )}, and they’ll appear in Remind once they accept.`
      : '';
  return [directAddCopy, invitationCopy].join(' ');
}

class UsersInvite extends React.Component<Props, State> {
  state = {
    loading: false,
    errorCountFromServer: 0,
    directAddSuccessCountFromServer: 0,
    invitationSuccessCountFromServer: 0,
  };

  componentDidMount = () => {
    const entityInfo = this.getEntityInformation(this.props.group);

    this.props.setMaxRowsForEntity(entityInfo.model);
  };

  submitDirectAddParticipants = () => {
    this.setState({ loading: true });
    const { usersByRole, directAddUsers, group, onClose } = this.props;

    const users = [];

    DIRECT_ADD_USER_ROLES.forEach(role => {
      each(this.packageUsers(role, usersByRole[`${role}s`]), user => users.push(user));
    });

    const entity = this.getEntityInformation(group);
    directAddUsers(entity.model, entity.id, users)
      .then(({ data: { directAddUsers: resp } }) => {
        this.setState({ loading: false });
        if (resp.error) {
          this.props.showToast(resp.error.message, 'error');
        } else {
          const { numDirectAddSuccesses, numInvitationSuccesses, usersWithErrors } = resp.info;

          if (usersWithErrors.length > 0) {
            this.props.handleServerErrors(usersWithErrors);
            this.setState({
              errorCountFromServer: usersWithErrors.length,
              directAddSuccessCountFromServer: numDirectAddSuccesses,
              invitationSuccessCountFromServer: numInvitationSuccesses,
            });
          } else {
            this.props.showToast(
              successMessage(numDirectAddSuccesses, numInvitationSuccesses),
              'success'
            );
            onClose();
          }
        }
      })
      .catch(e => {
        this.setState({ loading: false });
        this.props.showToast('An error occurred while adding users.', 'error');
        console.debug(e, e && e.stack);
        console.error('UsersInvite mutation error');
      });
  };

  getEntityInformation(group: Group): { model: 'organization' | 'group', id: number } {
    const orgId = group.getStaffClassOrgId();

    const model = orgId ? 'organization' : 'group';

    const id = orgId || group.id;

    return {
      model,
      id,
    };
  }

  verbForUserNumber = (num: number): string => {
    return num === 1 ? 'was' : 'were';
  };

  verbalErrorWithPrefix = (num: number): string => {
    return num === 1 ? 'An error' : 'Errors';
  };

  filledRow = (row: UserRow) => {
    return !userRowEmpty(row);
  };

  packageUsers(role: string, userCollection: Array<UserRow>): Array<DirectAddUserData> {
    function convertDevices(devices: Array<string>) {
      return devices
        .filter(device => device.length > 0)
        .map(device => RecipientParser.newToken(device).address);
    }

    return userCollection.filter(this.filledRow).map(user => {
      return {
        name: user.name.value,
        devices: convertDevices([user.primaryDevice.value, user.secondaryDevice.value]),
        role,
      };
    });
  }

  getSubmitDisabled = (totalErrors: number) => {
    const { loading } = this.state;
    const { containsData } = this.props;

    return loading || totalErrors !== 0 || !containsData;
  };

  getErrorSummary = (): ErrorSummary => {
    const { students, parents, teachers } = this.props.usersByRole;
    return {
      student: students.filter(userRow => !userRow.userValid).length,
      parent: parents.filter(userRow => !userRow.userValid).length,
      teacher: teachers.filter(userRow => !userRow.userValid).length,
    };
  };

  getTotalErrors = (summary: ErrorSummary): number => {
    return values(summary).reduce((acc, val) => acc + val);
  };

  getTabsWithErrorIndicator = (summary: ErrorSummary): string[] => {
    return Object.keys(summary).filter(role => summary[role] !== 0);
  };

  getAlertElement = (group: Group): React.Element => {
    const entityInfo = this.getEntityInformation(group);

    if (entityInfo.model === 'group') {
      return (
        <Text color="thunder" element="p" size="xxsmall">
          Only {MAXIMUM_ROWS.group} people can be added at one time.
        </Text>
      );
    } else {
      return (
        <Text color="thunder" element="p" size="xxsmall">
          Only {MAXIMUM_ROWS.organization} people can be added at one time. To add more people,
          learn about{' '}
          <Link to={sisSyncPath(entityInfo.id)} target="_blank">
            rostering via sis sync
          </Link>
          .
        </Text>
      );
    }
  };

  getServerErrorText = (
    errorCountFromServer: number,
    directAddSuccessCountFromServer: number,
    invitationSuccessCountFromServer: number
  ) => {
    const successStatement = successMessage(
      directAddSuccessCountFromServer,
      invitationSuccessCountFromServer
    );
    const failureStatement = `${this.verbalErrorWithPrefix(
      errorCountFromServer
    )} ${this.verbForUserNumber(
      errorCountFromServer
    )} detected for ${errorCountFromServer} ${pluralize('person', errorCountFromServer)}:`;

    const pronoun = errorCountFromServer === 1 ? `it` : `them`;

    return (
      <React.Fragment>
        {successStatement} {failureStatement} Fix {pronoun} and resubmit to send their invitations.{' '}
        <Link to={LinkConstants.DIRECT_ADD_ERROR} color="white" target="_blank" underline>
          Learn more
        </Link>
      </React.Fragment>
    );
  };

  getValidationErrorText = (totalErrors: number) => {
    const countText =
      totalErrors === 1
        ? `${totalErrors} error needs updating.`
        : `${totalErrors} errors need updating.`;
    return `${countText} Make sure each user has valid contact information.`;
  };

  renderSubmitButton = (submitDisabled: boolean, canAddPeople: boolean, loading: boolean) => {
    const submitButton = (<Button
      disabled={submitDisabled}
      loading={loading}
      onClick={this.submitDirectAddParticipants}
      data-test-selector="invite-modal-add-people-button">
      Add people
    </Button>);

    if (!canAddPeople) {
      return (
        <LayerTrigger
          layer={<Tooltip>{`You can't invite others because the class owner requires approval for participants to join.`}</Tooltip>}
          delay={50}
          placement="left"
        >
          <div>
            {submitButton}
          </div>
        </LayerTrigger>
      );
    } else {
      return submitButton;
    }
  }

  render() {
    const { group, supportSms, totalNumberOfUsersToAdd } = this.props;
    const {
      loading,
      errorCountFromServer,
      invitationSuccessCountFromServer,
      directAddSuccessCountFromServer,
    } = this.state;

    // Do this once and pass around the summary because it's
    // potentially an expensive operation
    const errorSummary = this.getErrorSummary();

    const totalErrors = this.getTotalErrors(errorSummary);
    const canAddPeople = group.canAddPeople();
    const submitDisabled = this.getSubmitDisabled(totalErrors) || !canAddPeople;
    const tabsToFlagWithErrors = this.getTabsWithErrorIndicator(errorSummary);

    const showErrorNotice = totalErrors > 0 || errorCountFromServer > 0;

    const fileTypeProps = {
      gutter: 'xxsmall',
      iconSize: 'inherit',
      inline: true,
    };

    const currentUser = CurrentUser.isSignedIn() ? CurrentUser.getUser() : undefined;
    const hasFreeProductLimitFeature =
      currentUser && currentUser.hasFeature('free_product_limits.web.apr-19');

    return (
      <Text align="left" element="section" className="UsersInvite">
        <TitleHeader
          title="Enter contacts or copy/paste from a spreadsheet"
          subtext={
            <React.Fragment>
              Copy and paste directly from{' '}
              <TextWithIcon {...fileTypeProps} icon="google-sheets" iconColor="google-sheets">
                Google Sheets
              </TextWithIcon>{' '}
              or{' '}
              <TextWithIcon {...fileTypeProps} icon="microsoft-excel" iconColor="microsoft-excel">
                Microsoft Excel
              </TextWithIcon>
              .
            </React.Fragment>
          }
          textAlign="left"
        />
        {currentUser &&
          group.isOwnedByUser(currentUser.id) &&
          hasFreeProductLimitFeature && (
            <FreeClassMembershipLimitNoticeContainer
              groupUuid={group.getUuid()}
              groupCode={group.getCodeOrError()}
              numberOfUsersToAdd={totalNumberOfUsersToAdd}
            />
          )}
        <Row flush={!totalErrors}>
          <Tabs flush={false}>
            {DIRECT_ADD_USER_ROLES.map(item => (
              <TabPanel
                key={item}
                indicator={tabsToFlagWithErrors.includes(item)}
                title={capitalize(pluralize(item))}>
                <UsersParticipantsTableInput
                  userType={item}
                  supportSms={supportSms}
                  alert={this.getAlertElement(group)}
                />
              </TabPanel>
            ))}
          </Tabs>
        </Row>
        {showErrorNotice ? (
          <Row>
            <Notice color="lava" noShadow>
              {errorCountFromServer > 0
                ? this.getServerErrorText(
                  errorCountFromServer,
                  directAddSuccessCountFromServer,
                  invitationSuccessCountFromServer
                )
                : this.getValidationErrorText(totalErrors)}
            </Notice>
          </Row>
        ) : (
            <Divider size="medium" />
          )}
        {this.renderSubmitButton(submitDisabled, canAddPeople, loading)}
      </Text>
    );
  }
}

const withMutation = graphql(
  gql`
    mutation directAddUsers($input: DirectAddUsersInput!) {
      directAddUsers(input: $input) {
        error {
          message
        }
        info {
          numDirectAddSuccesses
          numInvitationSuccesses
          usersWithErrors {
            address
            errors
          }
        }
      }
    }
  `,
  {
    props: ({ mutate }) => ({
      directAddUsers(targetModel: string, targetId: number, users: Array<DirectAddUserData>) {
        const input = { targetModel, targetId, users };
        return mutate({ variables: { input } });
      },
    }),
  }
);

function mapStateToProps(state) {
  const { students, parents, teachers } = state.directAddForm;
  const filledStudents = students.filter(student => !userRowEmpty(student));
  const filledParents = parents.filter(parent => !userRowEmpty(parent));
  const filledTeachers = teachers.filter(teacher => !userRowEmpty(teacher));

  return {
    usersByRole: {
      students,
      parents,
      teachers,
    },
    containsData: state.directAddForm.containsData,
    totalNumberOfUsersToAdd: filledStudents.length + filledParents.length + filledTeachers.length,
  };
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ showToast, setMaxRowsForEntity, handleServerErrors }, dispatch);
}

export { UsersInvite };

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withMutation
)(UsersInvite);
